import { useState } from "react";
import type { ReactNode } from "react";
import { Button, Cluster, HintMark } from "@salesapp/ui";

export function EditInput({ label, value, onChange, options, type = "text", disabled, min, max, children, tooltip }: {
  label: string; value?: string | number; onChange: (v: string) => void; options?: (string | { value: string; label: string })[]; type?: string; disabled?: boolean; min?: number; max?: number; children?: ReactNode; tooltip?: string;
}) {
  return <label className="designer-input"><span style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-1)" }}>{label}{tooltip ? <HintMark label={`About ${label}`} text={tooltip} /> : null}</span>{options ? <select value={value ?? ""} disabled={disabled} onChange={e => onChange(e.target.value)}>{options.map(o => <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>{typeof o === "string" ? o : o.label}</option>)}</select>
    : type === "textarea" ? <textarea rows={4} value={value ?? ""} disabled={disabled} onChange={e => onChange(e.target.value)} />
      : <input type={type} min={min} max={max} value={value ?? ""} disabled={disabled} onChange={e => onChange(e.target.value)} />}{children}</label>;
}

export function Toggle({ label, checked, onChange, disabled, tooltip }: { label: string; checked?: boolean; onChange: (v: boolean) => void; disabled?: boolean; tooltip?: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-1)" }}>
      <label className="designer-toggle"><input type="checkbox" checked={!!checked} disabled={disabled} onChange={e => onChange(e.target.checked)} />{label}</label>
      {tooltip ? <HintMark label={`About ${label}`} text={tooltip} /> : null}
    </span>
  );
}

export const CHART_COLOR_TOKENS = [
  "--color-chart-1", "--color-chart-2", "--color-chart-3", "--color-chart-4",
  "--color-chart-5", "--color-chart-6", "--color-chart-7", "--color-chart-8",
] as const;

export type SwatchOption = string | {
  value: string;
  emitValue?: string;
};

export type SwatchGroup = {
  label: string;
  options: SwatchOption[];
};

function colourValue(value?: string): string {
  if (value?.startsWith("#") && /^#[0-9a-fA-F]{6}$/.test(value)) return value.toLowerCase();
  if (value?.startsWith("var(") && typeof document !== "undefined") {
    const probe = document.createElement("span");
    probe.style.color = value;
    document.body.appendChild(probe);
    const rgb = getComputedStyle(probe).color;
    probe.remove();
    const match = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (match) {
      return `#${[match[1], match[2], match[3]].map((n) => Number(n).toString(16).padStart(2, "0")).join("")}`;
    }
  }
  return `#${"0".repeat(6)}`;
}

export function ColorSwatchRow({
  label,
  value,
  onChange,
  emitHex = false,
  tooltip,
  swatchGroups,
}: {
  label: string;
  value?: string;
  onChange: (v: string) => void;
  /** When true, palette picks resolve to `#rrggbb` (needed for APIs that only store hex). */
  emitHex?: boolean;
  tooltip?: string;
  swatchGroups?: SwatchGroup[];
}) {
  const groups = swatchGroups ?? [{ label: "", options: CHART_COLOR_TOKENS.map(t => `var(${t})`) }];
  const options = groups.flatMap((group) => group.options);
  const optionValue = (option: SwatchOption) => typeof option === "string" ? option : option.value;
  const optionEmitValue = (option: SwatchOption) => typeof option === "string" ? undefined : option.emitValue;
  const resolvedValue = colourValue(value);
  const matchingOption = options.find((option) => {
    const emitValue = optionEmitValue(option)?.toLowerCase();
    return (!!emitValue && emitValue === value?.toLowerCase()) || colourValue(optionValue(option)) === resolvedValue;
  });
  const isToken = !!value && options.some((option) => optionValue(option) === value);
  const isCustom = !!value && !isToken && !(emitHex && matchingOption);
  return (
    <label className="designer-input designer-swatch-row">
      <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-1)" }}>
        {label}
        {tooltip ? <HintMark label={`About ${label}`} text={tooltip} /> : null}
      </span>
      <span className="designer-swatch-row__swatches">
        {groups.map((group) => (
          <span className="designer-swatch-group" key={group.label || "default"}>
            {group.label ? <span className="designer-swatch-group__label">{group.label}</span> : null}
            <span className="designer-swatch-group__buttons">
              {group.options.map(option => {
                const v = optionValue(option);
                const emitValue = optionEmitValue(option);
                const pressed = emitHex
                  ? matchingOption === option || (!!emitValue && emitValue.toLowerCase() === value?.toLowerCase())
                  : value === v;
                return (
                <button
                  key={v}
                  type="button"
                  className="designer-swatch"
                  aria-label={`${label}: ${group.label ? `${group.label} ` : ""}${v}`}
                  aria-pressed={pressed}
                  style={{ backgroundColor: v }}
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => onChange(emitHex ? (emitValue ?? colourValue(v)) : v)}
                />
              );
              })}
            </span>
          </span>
        ))}
        <input
          type="color"
          className="designer-colour-input"
          title={`${label}: custom colour`}
          aria-label={`${label}: custom colour`}
          value={colourValue(isCustom ? value : undefined)}
          onChange={e => onChange(e.target.value)}
        />
      </span>
    </label>
  );
}

export function FieldPicker({ label, value, options, onChange, allowBlank, disabled, tooltip }: {
  label: string;
  value?: string | null;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  allowBlank?: string;
  disabled?: boolean;
  tooltip?: string;
}) {
  const [search, setSearch] = useState("");
  const term = search.trim().toLowerCase();
  const filtered = term ? options.filter(o => o.label.toLowerCase().includes(term) || o.value.toLowerCase().includes(term)) : options;
  return (
    <label className="designer-input designer-field-picker">
      <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-1)" }}>
        {label}
        {tooltip ? <HintMark label={`About ${label}`} text={tooltip} /> : null}
      </span>
      {options.length > 8 && (
        <input
          type="search"
          className="designer-field-picker__search"
          placeholder="Search fields…"
          value={search}
          disabled={disabled}
          onChange={e => setSearch(e.target.value)}
        />
      )}
      <select value={value ?? ""} disabled={disabled} onChange={e => onChange(e.target.value)}>
        {allowBlank !== undefined && <option value="">{allowBlank}</option>}
        {filtered.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

export function PositionGrid({ colStart, colSpan, onChange }: {
  colStart?: number;
  colSpan?: number;
  onChange: (patch: { colStart: number; colSpan: number }) => void;
}) {
  const start = colStart || 1;
  const span = colSpan || 1;
  const overflow = start + span - 1 > 7;
  const cells = [1, 2, 3, 4, 5, 6, 7];
  return (
    <div className="designer-position-grid">
      <span className="designer-position-grid__label">Position <small>(where this item sits in its row's 7-column grid)</small></span>
      <div className="designer-position-grid__selects">
        <label>
          Col start
          <select value={start} onChange={e => onChange({ colStart: Number(e.target.value), colSpan: span })}>
            {cells.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label>
          Col span
          <select value={span} onChange={e => onChange({ colStart: start, colSpan: Number(e.target.value) })}>
            {cells.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>
      <div className="designer-position-grid__strip">
        {cells.map(c => {
          const filled = c >= start && c < start + span;
          const cls = filled ? (overflow ? "designer-position-grid__cell--overflow" : "designer-position-grid__cell--filled") : "";
          return <span key={c} className={`designer-position-grid__cell ${cls}`} />;
        })}
      </div>
      {overflow && <div className="designer-position-grid__hint">⚠ overflows the row's 7-column grid</div>}
    </div>
  );
}

export { Button, Cluster };
